import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type RemoteIntroPromoAnalyticsPorts,
  type TrackRemoteIntroPromoClicked,
  type TrackRemoteIntroPromoDismissed,
  type TrackRemoteIntroPromoViewed,
} from '@heidi/growth/contracts';

/**
 * The app-side emit host for the Heidi Remote intro promo (`@heidi/growth`).
 *
 * Ruling 24: the three promo surfaces decide WHEN each event fires — the
 * once-per-cohort impression guard, the open→close transition that produces a
 * dismiss, the storefront URL a modal click resolves — and supply the payload;
 * these three functions add the `event_name`, the destination routing, and
 * nothing else.
 *
 * These were `dispatch.analyticsScribe.trackEventV2` calls inside
 * `src/features/growth/remote-intro-promo/analytics.ts`, which took the dispatch
 * as a parameter. Same events, same props, same routing — what changes is that
 * the store is out of four modules destined for `@heidi/growth`, and the emit is
 * on the sanctioned `track()` path rather than the Rematch bus being severed
 * (Wave 1 · P2).
 *
 * ROUTING IS PRESERVED, NOT TIDIED: `trackEventV2` passed no destination options,
 * so none are passed here. None of the three names is on the Snowplow denylist
 * (`@/lib/analytics/snowplow-suppression`), which the pipeline applies by event
 * name in `enrich()`, so no `options` object carries suppression and none should
 * acquire one.
 *
 * MODULE-LEVEL FUNCTIONS ON PURPOSE. `trackRemoteIntroPromoViewed` is a
 * dependency of an effect in all three surfaces, so an emit whose identity
 * changed per render would re-fire it. A module binding has one identity for the
 * life of the process, which is stricter than the `useCallback` the seam needs.
 *
 * WHY THE ENUM MEMBER AND NOT `remoteIntroPromoViewed.name`. Under D9 ruling 27
 * the name's one declaration site is
 * `packages/growth/src/contributions/analytics.ts`, and an emitter is supposed to
 * read it from there. This one cannot: `event_name` discriminates the app's
 * `TrackingEventV2` union, whose members are typed by string ENUM MEMBERS, and
 * TypeScript does not accept a string literal in an enum member's place — passing
 * the descriptor's `.name` would not compile, and widening the union's key to
 * `string` would delete the event/props check that makes these call sites safe.
 * So the enum member stays and the agreement is ASSERTED instead, in this
 * module's suite, which is what makes the enum a binding rather than a second
 * spelling.
 */

/** Fire once per surface mount when the promo is shown to an eligible user. */
export const trackRemoteIntroPromoViewed: TrackRemoteIntroPromoViewed = (
  surface,
  cohort
) => {
  track({
    event_name: TrackingEventName.RemoteIntroPromoViewed,
    props: { surface, cohort },
  });
};

/**
 * Fire when the user clicks the primary CTA on any surface.
 *
 * The two extras are spread conditionally rather than passed through as
 * `undefined`, which preserves the pre-move payload exactly: a banner or
 * menu-card click sends `{ surface, cohort }` with no `entry_surface` key at all,
 * and a payload carrying `entry_surface: undefined` is a different row.
 */
export const trackRemoteIntroPromoClicked: TrackRemoteIntroPromoClicked = (
  surface,
  cohort,
  extras = {}
) => {
  track({
    event_name: TrackingEventName.RemoteIntroPromoClicked,
    props: {
      surface,
      cohort,
      ...(extras.entrySurface ? { entry_surface: extras.entrySurface } : {}),
      ...(extras.destinationUrl ? { destination_url: extras.destinationUrl } : {}),
    },
  });
};

/** Fire when the user dismisses a surface (banner X, modal close). */
export const trackRemoteIntroPromoDismissed: TrackRemoteIntroPromoDismissed = (
  surface,
  cohort
) => {
  track({
    event_name: TrackingEventName.RemoteIntroPromoDismissed,
    props: { surface, cohort },
  });
};

/**
 * The three seams as one bundle, for the surface that composes the promo once its
 * root makes its class-A move.
 *
 * A module constant rather than a `useMemo`, so it is the same object on every
 * render and cannot be the thing that re-fires an impression effect.
 */
export const remoteIntroPromoAnalyticsPorts: RemoteIntroPromoAnalyticsPorts = {
  trackViewed: trackRemoteIntroPromoViewed,
  trackClicked: trackRemoteIntroPromoClicked,
  trackDismissed: trackRemoteIntroPromoDismissed,
};
